import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NIL as NIL_UUID } from "uuid";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Grid,
  Tooltip,
  Button,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import GetAppIcon from "@material-ui/icons/GetApp";
import { FolderZip } from "@mui/icons-material";
import CompanyHeading from "../../../../theming/CompanyHeading";
import {
  clearOrganizationEventParticipationsData,
  getOrganizationEventParticipationsByOrganizationId,
} from "../../../../../redux/organizationEventParticipations/action";
import { AppState } from "../../../../../redux";
import { OrganizationEventParticipationsData } from "../../../../../models/organizationEventParticipationsData";
import {
  ParticipationStatus,
  statusMainTranslationKeys,
} from "../../../../../models/enums/participationStatus.enum";
import Loader from "../../../../theming/loader/Loader";
import {
  BookingsManagementProps,
  EventParticipationDetail,
} from "./BookingManagement.interfaces";
import { useTableRowState } from "../../../../../hooks/useTableRowState";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import AddOrganizationBooking from "./AddOrganizationBooking";
import createSafeHTML from "../../../../../utils/createSafeHTML";
import CircularProgressWithLabel from "../../../../forms/CustomCircularProgress";
import { InfoTooltip, useTooltipHoverStyles } from "../../../../forms/CustomInfoTooltip";
import EventParticipationInfoBoxTooltip from "./EventParticipationInfoBoxTooltip";

/**
 * Component for managing organization event participations and displaying bookings in a table format.
 *
 * @param {BookingsManagementProps} props - Props for the component.
 * @property {string} props.organizationId - The ID of the organization to fetch event participations for.
 * @returns {JSX.Element} The BookingsManagement component.
 */
export const BookingsManagement: React.FC<BookingsManagementProps> = ({
  organizationId,
}) => {
  const { t } = useTranslation(["enums", "organization", "snackbars", "common"]);

  const dispatch = useDispatch();

  const [expandedRows, toggleRow] = useTableRowState("expandedTableRows");
  const [isOpenAddBookingSection, setIsOpenAddBookingSection] = useState(false);

  const [sortedByEvent, setSortedByEvent] = useState(() => {
    const storedValue = localStorage.getItem("sortedByEvent");
    return storedValue ? JSON.parse(storedValue) : true;
  });

  const { eventSortedData, userSortedData, totalProgressPercentage, isLoading } =
    useSelector((state: AppState) => state.organizationEventParticipations);

  const isPerBookingBilling = useSelector(
    (state: AppState) => state.metaData.metaData.is_per_booking_billing
  );
  const cancellationFee = useSelector(
    (state: AppState) => state.metaData.metaData.cancellation_fee
  );
  const isOrganizationInvoiceEnabled = useSelector(
    (state: AppState) =>
      state.featureFlagging.featureFlags?.organization_invoice_is_enabled
  );

  const organizationEventParticipationsData = sortedByEvent
    ? eventSortedData
    : userSortedData;

  const tooltipClass = useTooltipHoverStyles();

  /**
   * Fetches event participations for the given organization ID when the
   * component is mounted or the organization ID changes or when the sorting changes.
   */
  useEffect(() => {
    dispatch(clearOrganizationEventParticipationsData());

    dispatch(
      getOrganizationEventParticipationsByOrganizationId(organizationId, sortedByEvent)
    );
  }, [dispatch, organizationId, sortedByEvent, isOpenAddBookingSection]);

  useEffect(() => {
    localStorage.setItem("sortedByEvent", sortedByEvent);
  }, [sortedByEvent]);

  /**
   * Maps the participation status to its corresponding translation key.
   *
   * @param {string} status - The status to map.
   * @returns {string} The translated key for the status.
   */
  const mapToParticipationStatus = (status: string): string => {
    return statusMainTranslationKeys[status];
  };

  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    newSortValue: boolean
  ) => {
    if (newSortValue !== null) {
      setSortedByEvent(newSortValue);
    }
  };

  /**
   * Array of status column keys, representing different participation statuses.
   * @type {string[]}
   */
  const statusColumns: string[] = [
    String(ParticipationStatus.Booked),
    String(ParticipationStatus.Participated),
    String(ParticipationStatus.NotParticipated),
  ];

  return (
    <Box>
      <Box>
        <CompanyHeading>
          {t("bookings.manageBookingsTitle", { ns: "organization" })}
        </CompanyHeading>
        <Grid container alignItems="center" justify-content="space-between" spacing={2}>
          {!isOpenAddBookingSection && (
            <Grid item xs={12} sm>
              <Typography>
                <Trans>{t("bookings.addBookingDesc", { ns: "organization" })}</Trans>
              </Typography>
            </Grid>
          )}
          {!isOpenAddBookingSection && (
            <Grid item xs={12} sm="auto">
              <Button
                onClick={() => setIsOpenAddBookingSection(!isOpenAddBookingSection)}
                variant={isOpenAddBookingSection ? "outlined" : "contained"}
                color="primary"
              >
                {t("bulkBookings.addBooking", { ns: "organization" })}
              </Button>
            </Grid>
          )}
        </Grid>
        {isPerBookingBilling && isOrganizationInvoiceEnabled ? (
          <Box mt={{ xs: 4, sm: 2 }}>
            <Typography>
              <Trans>
                {t("bookings.perBookingBillingNotice", {
                  ns: "organization",
                  cancellation_fee: cancellationFee,
                })}
              </Trans>
            </Typography>
          </Box>
        ) : null}
      </Box>
      {isOpenAddBookingSection ? (
        <AddOrganizationBooking
          organizationId={organizationId}
          onClose={() => setIsOpenAddBookingSection(false)}
        />
      ) : null}

      {!isOpenAddBookingSection ? (
        <Box mt={7} mb={3}>
          <CompanyHeading>
            {t("bookings.manageBookingsDescTitle", { ns: "organization" })}
          </CompanyHeading>
        </Box>
      ) : null}
      {!isOpenAddBookingSection ? (
        <Grid>
          <Typography>
            {t("bookings.manageBookingsDesc", { ns: "organization" })}
          </Typography>
          {!isOpenAddBookingSection &&
          organizationEventParticipationsData.length !== 0 &&
          totalProgressPercentage !== null ? (
            <Box
              boxShadow={1}
              borderColor="primary"
              borderRadius={8}
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              px={1.5}
              py={1.5}
              mt={3}
              mb={5}
              style={{ gap: "1.5rem", width: "fit-content" }}
            >
              <Typography component="div" style={{ fontWeight: 600 }}>
                {t("bookings.totalProgressPercentage", { ns: "organization" })}
              </Typography>
              <CircularProgressWithLabel
                value={totalProgressPercentage}
                size="4rem"
                thickness={4}
              />
            </Box>
          ) : null}
          {organizationEventParticipationsData.length !== 0 ? (
            <Box style={{ marginTop: "1.25rem", display: "flex", alignItems: "center" }}>
              <ToggleButtonGroup
                value={sortedByEvent}
                exclusive
                onChange={handleToggleChange}
                aria-label="Nach Kursen oder Mitgliedern sortieren"
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "1.5rem",
                }}
              >
                <ToggleButton
                  value={false}
                  aria-label="Mitglieder"
                  style={{ textTransform: "none", height: "100%" }}
                >
                  {t("bookings.members", { ns: "organization" })}
                </ToggleButton>
                <ToggleButton
                  value
                  aria-label="Kurse"
                  style={{ textTransform: "none", height: "100%" }}
                >
                  {t("bookings.courses", { ns: "organization" })}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : null}
          <Box style={{ width: "100%", marginTop: "1.25rem" }}>
            {isLoading ? (
              <Loader />
            ) : organizationEventParticipationsData.length === 0 ? (
              <Typography>
                {t("bookings.manageBookingsNoBookings", { ns: "organization" })}
              </Typography>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TableContainer component={Paper}>
                    <Table
                      aria-label="collapsible table"
                      style={{ tableLayout: "auto", width: "100%" }}
                    >
                      <TableBody>
                        {organizationEventParticipationsData.map(
                          (eventParticipation: OrganizationEventParticipationsData) => {
                            const {
                              name,
                              status = {},
                              certificates_zip_url,
                              progress_percentage,
                            } = eventParticipation;
                            const isExpanded = expandedRows[name] || false;

                            return (
                              <React.Fragment key={name}>
                                <TableRow>
                                  <TableCell
                                    align="left"
                                    style={{
                                      wordBreak: "break-word",
                                      fontWeight: "bold",
                                    }}
                                    dangerouslySetInnerHTML={createSafeHTML(name)}
                                  />
                                  <TableCell align="right">
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="flex-end"
                                      style={{ gap: "0.5rem" }}
                                    >
                                      <CircularProgressWithLabel
                                        value={progress_percentage}
                                        size="48px"
                                        thickness={3}
                                      />
                                      <Tooltip
                                        title={
                                          certificates_zip_url
                                            ? t(
                                                "bookings.tooltips.downloadCertificatesZip",
                                                { ns: "organization" }
                                              )
                                            : t("bookings.tooltips.noCertificatesZip", {
                                                ns: "organization",
                                              })
                                        }
                                        placement="top"
                                        arrow
                                      >
                                        <span>
                                          <IconButton
                                            disabled={!certificates_zip_url}
                                            href={certificates_zip_url || ""}
                                          >
                                            <FolderZip
                                              color={
                                                certificates_zip_url
                                                  ? "inherit"
                                                  : "disabled"
                                              }
                                            />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <IconButton
                                        aria-label="expand row"
                                        size="small"
                                        onClick={() => toggleRow(name)}
                                      >
                                        {isExpanded ? (
                                          <KeyboardArrowUpIcon />
                                        ) : (
                                          <KeyboardArrowDownIcon />
                                        )}
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                                {isExpanded ? (
                                  <TableRow>
                                    <TableCell
                                      colSpan={2}
                                      style={{ paddingBottom: 0, paddingTop: 0 }}
                                    >
                                      <Collapse
                                        in={isExpanded}
                                        timeout="auto"
                                        unmountOnExit
                                      >
                                        <Box margin={1}>
                                          <Table
                                            size="small"
                                            aria-label="expanded-status"
                                            style={{
                                              tableLayout: "auto",
                                              width: "100%",
                                            }}
                                          >
                                            <TableHead>
                                              <TableRow>
                                                {statusColumns.map((statusKey) => (
                                                  <TableCell
                                                    key={statusKey}
                                                    style={{
                                                      wordBreak: "break-word",
                                                      whiteSpace: "nowrap",
                                                      fontWeight: "bold",
                                                      verticalAlign: "top",
                                                    }}
                                                  >
                                                    <Trans>
                                                      {t(
                                                        mapToParticipationStatus(
                                                          statusKey
                                                        ),
                                                        {
                                                          ns: "enums",
                                                        }
                                                      )}
                                                    </Trans>
                                                  </TableCell>
                                                ))}
                                                <TableCell
                                                  style={{
                                                    wordBreak: "break-word",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                    verticalAlign: "top",
                                                  }}
                                                >
                                                  {t("bookings.certificate", {
                                                    ns: "organization",
                                                  })}
                                                </TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {Object.keys(status).map((statusKey) => {
                                                const eventOrUsers: EventParticipationDetail[] =
                                                  (status[statusKey] || []).map(
                                                    // eslint-disable-next-line camelcase
                                                    (event_or_user) =>
                                                      // eslint-disable-next-line camelcase
                                                      typeof event_or_user === "string"
                                                        ? {
                                                            // eslint-disable-next-line camelcase
                                                            event_or_user,
                                                            // eslint-disable-next-line camelcase
                                                            certificate_link: null,
                                                            evp_additional_info: {
                                                              order_date: "",
                                                              completed_on: null,
                                                              valid_until: null,
                                                            },
                                                          }
                                                        : // eslint-disable-next-line camelcase
                                                          event_or_user
                                                  );

                                                return eventOrUsers.map(
                                                  (
                                                    eventOrUser: EventParticipationDetail
                                                  ) => (
                                                    <TableRow
                                                      key={`${statusKey}-${NIL_UUID}`}
                                                    >
                                                      {statusColumns.map((columnKey) => (
                                                        <TableCell
                                                          key={columnKey}
                                                          style={{
                                                            wordBreak: "break-word",
                                                            whiteSpace: "pre-wrap",
                                                            width: `${100 / statusColumns.length}%`,
                                                          }}
                                                        >
                                                          {columnKey === statusKey ? (
                                                            <InfoTooltip
                                                              title={
                                                                <EventParticipationInfoBoxTooltip
                                                                  order_date={
                                                                    eventOrUser
                                                                      .evp_additional_info
                                                                      ?.order_date
                                                                  }
                                                                  completed_on={
                                                                    eventOrUser
                                                                      .evp_additional_info
                                                                      ?.completed_on
                                                                  }
                                                                  valid_until={
                                                                    eventOrUser
                                                                      .evp_additional_info
                                                                      ?.valid_until
                                                                  }
                                                                />
                                                              }
                                                              placement="right"
                                                              arrow
                                                            >
                                                              <span
                                                                className={
                                                                  tooltipClass.tooltipHover
                                                                }
                                                              >
                                                                {
                                                                  eventOrUser.event_or_user
                                                                }
                                                              </span>
                                                            </InfoTooltip>
                                                          ) : (
                                                            ""
                                                          )}
                                                        </TableCell>
                                                      ))}
                                                      <TableCell
                                                        style={{
                                                          wordBreak: "break-word",
                                                          whiteSpace: "pre-wrap",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        {eventOrUser.certificate_link ? (
                                                          <IconButton
                                                            size="small"
                                                            href={
                                                              eventOrUser.certificate_link
                                                            }
                                                          >
                                                            <GetAppIcon />
                                                          </IconButton>
                                                        ) : null}
                                                      </TableCell>
                                                    </TableRow>
                                                  )
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                ) : null}
                              </React.Fragment>
                            );
                          }
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </Box>
        </Grid>
      ) : null}
    </Box>
  );
};

export default BookingsManagement;
